import React, { useEffect, useState } from 'react';
import { SharedModal } from '../SharedModal';
import {
  applyFunnelTemplate,
  type CrmColumnCondition,
  type CrmFunnelTemplate,
} from '../../services/CrmAutomationService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import './crmFunnel.css';

const TEMPLATES: Array<{ id: CrmFunnelTemplate; title: string; columns: string }> = [
  {
    id: 'vendas',
    title: 'Funil de vendas (6 colunas)',
    columns: 'Atendimento → Qualificado → Reunião agendada → Proposta → Venda fechada · Perdido',
  },
  {
    id: 'simples',
    title: 'Atendimento → Venda fechada (2 colunas)',
    columns: 'Atendimento → Venda fechada',
  },
];

export interface FunnelTemplateModalProps {
  open: boolean;
  sectorId: number;
  onClose: () => void;
  /** Chamado depois de aplicar — o CRM recarrega o quadro. */
  onApplied: (conditions: CrmColumnCondition[]) => void;
}

const FunnelTemplateModal: React.FC<FunnelTemplateModalProps> = ({ open, sectorId, onClose, onApplied }) => {
  const [selected, setSelected] = useState<CrmFunnelTemplate>('vendas');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setApplying(false);
    }
  }, [open]);

  const handleApply = async () => {
    setApplying(true);
    setError(null);
    try {
      const conditions = await applyFunnelTemplate(sectorId, selected);
      onApplied(conditions);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg?.trim() || apiErrorMessage(e, 'Não foi possível aplicar o modelo.'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <SharedModal
      open={open}
      onClose={onClose}
      title="Modelos de funil"
      size="md"
      className="crm-automation-modal crm-template-modal"
      overlayClassName="crm-automation-modal-overlay"
    >
      <div className="crm-automation-modal__form">
        <p className="crm-automation-modal__hint">
          O modelo cria as colunas que faltam e preenche as condições vazias. Os cards e as condições que você já
          escreveu continuam como estão.
        </p>
        <div className="crm-template-modal__options" role="radiogroup" aria-label="Modelo de funil">
          {TEMPLATES.map((t) => (
            <label key={t.id} className={`crm-template-modal__option ${selected === t.id ? 'is-selected' : ''}`}>
              <input
                type="radio"
                name="crm-funnel-template"
                value={t.id}
                checked={selected === t.id}
                onChange={() => setSelected(t.id)}
              />
              <span className="crm-template-modal__option-body">
                <strong>{t.title}</strong>
                <span>{t.columns}</span>
              </span>
            </label>
          ))}
        </div>

        {error && <div className="crm-funnel-error" role="alert">{error}</div>}

        <div className="crm-automation-modal__actions">
          <button
            type="button"
            className="crm-automation-modal__btn crm-automation-modal__btn--secondary"
            onClick={onClose}
            disabled={applying}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="crm-automation-modal__btn crm-automation-modal__btn--primary"
            onClick={() => void handleApply()}
            disabled={applying}
          >
            {applying ? 'Aplicando…' : 'Aplicar modelo'}
          </button>
        </div>
      </div>
    </SharedModal>
  );
};

export default FunnelTemplateModal;
